import type { InvoiceQuota } from '../lib/quota';

/**
 * The plans that are sold, lowest first. `name` is how the lineup of 8-oct-2026 calls
 * them in every surface: the plan sheet, the upsell of a feature, the stamping error.
 * `limit` is what buying it gives per month, mirroring `invoiceplan.plan.monthly_limit`
 * (heru-fiscal-engine): the catalog does not carry it and the quota only knows the
 * user's current plan. What a user HAS always comes from `GET /cfdi/cupo`.
 */
const SELLING_PLANS = [
  { code: 'pro', name: 'Pro', limit: 40 },
  { code: 'plus', name: 'Plus', limit: 100 },
  { code: 'qr', name: 'Facturación con QR', limit: 500 },
] as const;

export type SellingPlanCode = (typeof SELLING_PLANS)[number]['code'];

interface InvoicingFeatureSpec {
  /** The lowest plan that includes it: every plan above includes it too. */
  plan: SellingPlanCode;
  /** How the plan sheet lists it. */
  label: string;
  /** What it does, in one line: the body of the upsell sheet. */
  description: string;
}

/**
 * Every feature of the invoicing plans, keyed by the code `GET /cfdi/cupo` lists in
 * `features` and the 403 `CFDI_FUNCION_FUERA_DEL_PLAN` names in `details.feature`. The
 * per-plan lists of the plan sheet are derived from here, in this order.
 */
const INVOICING_FEATURES = {
  client_email: {
    plan: 'pro',
    label: 'Envío por correo a tu cliente con el logo de heru',
    description: 'Tu cliente recibe su factura en su correo en cuanto la timbras.',
  },
  payment_complement: {
    plan: 'pro',
    label: 'Complementos de pago (PPD)',
    description: 'Factura en parcialidades y registra cada pago con su complemento.',
  },
  global_invoice: {
    plan: 'pro',
    label: 'Factura global',
    description: 'Junta en una sola factura tus ventas a público en general de un periodo.',
  },
  client_catalog: {
    plan: 'pro',
    label: 'Catálogo de clientes y productos',
    description: 'Guarda a tus clientes y tus productos para facturar sin volver a capturarlos.',
  },
  pdf_brand: {
    plan: 'plus',
    label: 'Tu logo en tus facturas',
    description: 'Tus facturas en PDF salen con tu logo y tus colores.',
  },
  email_brand: {
    plan: 'plus',
    label: 'Correo con tu logo y un mensaje tuyo',
    description: 'El correo que recibe tu cliente lleva tu logo y un mensaje tuyo.',
  },
  qr_page: {
    plan: 'qr',
    label: 'Tu página de facturación con QR',
    description: 'Tus clientes escanean tu QR y te piden su factura sin que captures nada.',
  },
  qr_poster: {
    plan: 'qr',
    label: 'Tu QR listo para imprimir',
    description: 'Descarga tu QR para ponerlo en tu mostrador.',
  },
  qr_approval: {
    plan: 'qr',
    label: 'Apruebas cada factura antes de timbrarla',
    description: 'Revisa cada factura que te piden por QR y apruébala para timbrarla.',
  },
  qr_ranking: {
    plan: 'qr',
    label: 'Ranking de tus clientes',
    description: 'Ve a qué clientes les facturas más cada mes.',
  },
} satisfies Record<string, InvoicingFeatureSpec>;

export type InvoicingFeature = keyof typeof INVOICING_FEATURES;

export const isInvoicingFeature = (value: unknown): value is InvoicingFeature =>
  typeof value === 'string' && value in INVOICING_FEATURES;

export const invoicingFeatureOf = (feature: InvoicingFeature): InvoicingFeatureSpec =>
  INVOICING_FEATURES[feature];

/** The display name of a sold plan code (`upgrade_options` speaks in codes). */
export const sellingPlanNameOf = (code: string | null | undefined): string | undefined =>
  SELLING_PLANS.find((plan) => plan.code === code)?.name;

/** How many invoices per month buying a sold plan gives; `undefined` for a code that is not sold. */
export const sellingPlanLimitOf = (code: string): number | undefined =>
  SELLING_PLANS.find((plan) => plan.code === code)?.limit;

/** The highest plan sold in the app: above it there is nothing to buy. */
export const isTopSellingPlan = (code: string): boolean => code === SELLING_PLANS[SELLING_PLANS.length - 1].code;

/** The sold plans above `code`, lowest first: what a bought plan can upgrade to. Empty for a code that is not sold. */
export const sellingPlansAbove = (code: string): string[] => {
  const tier = SELLING_PLANS.findIndex((plan) => plan.code === code);
  return tier < 0 ? [] : SELLING_PLANS.slice(tier + 1).map((plan) => plan.code);
};

/** The lowest sold plan that includes the feature, by name: «Esta función viene en Pro». */
export const lowestPlanNameWith = (feature: InvoicingFeature): string =>
  sellingPlanNameOf(INVOICING_FEATURES[feature].plan) ?? INVOICING_FEATURES[feature].plan;

interface InvoicingPlanFeatures {
  /** Replaces the server's plan name on the badge, when that name is not meant for the user. */
  label?: string;
  /** The plan one step below, by name: the sheet opens the list with «Todo lo de Pro». */
  includes?: string;
  features: string[];
}

/** What a sold plan adds over the one below it: its own features, in their order. */
const sellingPlanFeatures = (index: number): InvoicingPlanFeatures => {
  const plan = SELLING_PLANS[index];
  const own = Object.values(INVOICING_FEATURES)
    .filter((spec: InvoicingFeatureSpec) => spec.plan === plan.code)
    .map((spec) => spec.label);
  return { ...(index > 0 ? { includes: SELLING_PLANS[index - 1].name } : {}), features: own };
};

/**
 * What each invoicing plan adds, keyed by the server's plan code. How many invoices the
 * user's plan gives is NOT here: that comes from `GET /cfdi/cupo`, so a tax plan that includes
 * «Facturación con QR» capped at 100 reads its own cap. A code missing here (a new one,
 * or `basico` while it goes away) shows the server's name and no list.
 */
const INVOICING_PLAN_FEATURES: Partial<Record<string, InvoicingPlanFeatures>> = {
  gratis: { features: ['PDF y XML de cada factura'] },
  ...Object.fromEntries(SELLING_PLANS.map((plan, index) => [plan.code, sellingPlanFeatures(index)])),
  a_la_medida: {
    includes: sellingPlanNameOf('qr'),
    features: ['Varios RFC en la misma cuenta'],
  },
  legado_ilimitado: { label: 'Sin límite', features: [] },
};

/** Plans that sell the same features with another cap: RESICO's included QR plan is capped at 100. */
const SAME_FEATURES_AS: Partial<Record<string, string>> = { qr_100: 'qr' };

export const invoicingPlanFeaturesOf = (code: string) => INVOICING_PLAN_FEATURES[SAME_FEATURES_AS[code] ?? code];

export const sellingPlanIncludes = (code: string, feature: InvoicingFeature): boolean => {
  const tierOf = (planCode: string) => SELLING_PLANS.findIndex((plan) => plan.code === planCode);
  const tier = tierOf(SAME_FEATURES_AS[code] ?? code);
  return tier >= 0 && tier >= tierOf(INVOICING_FEATURES[feature].plan);
};

export const invoicingPlanLabelOf = (plan: InvoiceQuota['plan']) =>
  invoicingPlanFeaturesOf(plan.code)?.label ?? plan.name;
